/**
 * PIQ 8.5: Watchlist Chips
 * =========================
 * HOLD/MONITOR only, max 3 chips.
 * Derived from top opportunities, marketing, data health.
 */

import React from 'react';
import {
  TrendingUp,
  Megaphone,
  AlertCircle,
  Eye,
  ChevronRight,
} from 'lucide-react';
import type { InsightFeedItem, SourceType } from './operatorViewAdapter';

// ============================================================
// Types
// ============================================================

interface WatchlistChipsProps {
  opportunities: InsightFeedItem[];
  marketing: InsightFeedItem[];
  dataHealth: InsightFeedItem[];
  onChipClick?: (item: InsightFeedItem) => void;
  maxChips?: number;
}

// ============================================================
// Chip Component
// ============================================================

function WatchlistChip({ 
  item, 
  onClick 
}: { 
  item: InsightFeedItem; 
  onClick?: () => void;
}) {
  // Determine chip style based on category/type
  const isMarketing = item.sourceType === 'google' || item.sourceType === 'meta' || item.category === 'opportunities';
  const isDataHealth = item.category === 'data_health' || item.sourceType === 'system';
  const isOpportunity = item.category === 'opportunities' && !isMarketing;
  
  let chipClass = 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100';
  let Icon = Eye;
  
  if (isMarketing) {
    chipClass = 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100';
    Icon = Megaphone;
  } else if (isDataHealth) {
    chipClass = 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100';
    Icon = AlertCircle;
  } else if (isOpportunity) {
    chipClass = 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100';
    Icon = TrendingUp;
  }
  
  // Truncate title
  const label = item.summary.length > 40 
    ? item.summary.slice(0, 40) + '…' 
    : item.summary;

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-medium transition-all ${chipClass}`}
    >
      <Icon className="w-2.5 h-2.5 shrink-0" />
      <span className="truncate max-w-[180px]">{label}</span>
      {item.impactBand && (
        <span className="text-[9px] opacity-60">{item.impactBand}</span>
      )}
      <ChevronRight className="w-2.5 h-2.5 opacity-40 shrink-0" />
    </button>
  );
}

// ============================================================
// Main Component
// ============================================================

export function WatchlistChips({
  opportunities,
  marketing,
  dataHealth,
  onChipClick,
  maxChips = 3,
}: WatchlistChipsProps) {
  // Build chip list deterministically: 1 marketing, 1 opportunity, 1 data health (if exist)
  const chips: InsightFeedItem[] = [];
  
  // Priority: marketing first (Google/Meta), then opportunity, then data health
  if (marketing.length > 0) {
    chips.push(marketing[0]);
  }
  
  if (opportunities.length > 0 && chips.length < maxChips) {
    // Add first opportunity that isn't already in chips
    const opp = opportunities.find(o => !chips.some(c => c.id === o.id));
    if (opp) chips.push(opp);
  }
  
  if (dataHealth.length > 0 && chips.length < maxChips) {
    chips.push(dataHealth[0]);
  }
  
  // Fill remaining with more opportunities
  while (chips.length < maxChips && opportunities.length > chips.length) {
    const remaining = opportunities.filter(o => !chips.some(c => c.id === o.id));
    if (remaining.length > 0) {
      chips.push(remaining[0]);
    } else {
      break;
    }
  }
  
  if (chips.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      <Eye className="w-3 h-3 text-gray-400 shrink-0" />
      <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide shrink-0">Watching:</span>
      <div className="flex flex-wrap gap-1">
        {chips.map((item) => (
          <WatchlistChip
            key={item.id}
            item={item}
            onClick={() => onChipClick?.(item)}
          />
        ))}
      </div>
    </div>
  );
}

export default WatchlistChips;
